/**
 * GapConfigurationsCard — manage named GAP configurations.
 *
 * Phase 4.3.GAPCONF — mounted in the "GAP Parameters" section of AlmParametersPage.
 * Each GAP configuration defines: gap type, dimension, segmentation, currency, mode.
 * All ALM reports reference a configuration rather than storing raw params.
 */
import { useState } from 'react';
import { AlertCircle, Edit2, Plus, Shield, Trash2, X } from 'lucide-react';
import { Button }   from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input }    from '@/components/ui/input';
import { Label }    from '@/components/ui/label';
import { useAuthStore } from '@/lib/auth/store';
import { CubixApiError } from '@/lib/api/types';

import {
  useGapConfigurations,
  useCreateGapConfiguration,
  useUpdateGapConfiguration,
  useDeleteGapConfiguration,
  GAP_TYPE_LABELS,
  PRESENTATION_MODE_LABELS,
  ROW_DIMENSION_LABELS,
} from './gapConfigApi';
import type {
  GapConfiguration,
  GapConfigCreate,
  GapType,
  PresentationMode,
  RowDimension,
} from './gapConfigApi';

import { useReportConfigs } from './reportConfigApi';

// ── Helpers ───────────────────────────────────────────────────────────────────

const DEFAULT_FORM: GapConfigCreate = {
  name:             '',
  description:      '',
  gapType:          'LIQUIDITY',
  presentationMode: 'FLOW',
  rowDimension:     'INSTRUMENT_TYPE',
  segmentationId:   null,
  currency:         null,
};

function ApiErr({ err }: { err: CubixApiError | null }) {
  if (!err) return null;
  return (
    <p className="flex items-center gap-1 text-xs text-red-500 mt-1">
      <AlertCircle className="h-3 w-3" />
      {err.message ?? 'Erreur inattendue'}
    </p>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export function GapConfigurationsCard() {
  const isAdmin = useAuthStore(s => s.hasPermission('user:admin'));

  const { data: configs = [], isLoading } = useGapConfigurations();
  const { data: reportConfigs = [] }      = useReportConfigs();
  const createMut = useCreateGapConfiguration();
  const updateMut = useUpdateGapConfiguration();
  const deleteMut = useDeleteGapConfiguration();

  const [editing, setEditing]   = useState<GapConfiguration | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm]         = useState<GapConfigCreate>(DEFAULT_FORM);
  const [formErr, setFormErr]   = useState('');

  // Count how many report configs reference each gap config
  const usageMap: Record<string, number> = {};
  for (const rc of reportConfigs) {
    if (rc.gapConfigId) {
      usageMap[rc.gapConfigId] = (usageMap[rc.gapConfigId] ?? 0) + 1;
    }
  }

  function openCreate() {
    setEditing(null);
    setForm(DEFAULT_FORM);
    setFormErr('');
    setShowForm(true);
  }

  function openEdit(gc: GapConfiguration) {
    setEditing(gc);
    setForm({
      name:             gc.name,
      description:      gc.description ?? '',
      gapType:          gc.gapType,
      presentationMode: gc.presentationMode,
      rowDimension:     gc.rowDimension,
      segmentationId:   gc.segmentationId ?? null,
      currency:         gc.currency ?? null,
    });
    setFormErr('');
    setShowForm(true);
  }

  function closeForm() {
    setShowForm(false);
    setEditing(null);
  }

  function f(key: keyof GapConfigCreate, val: string | null) {
    setForm(prev => ({ ...prev, [key]: val }));
  }

  async function handleSubmit() {
    if (!form.name.trim()) { setFormErr('Le nom est requis'); return; }
    setFormErr('');
    try {
      if (editing) {
        await updateMut.mutateAsync({ id: editing.id, ...form });
      } else {
        await createMut.mutateAsync(form);
      }
      closeForm();
    } catch (e) {
      setFormErr((e as CubixApiError).message ?? 'Erreur');
    }
  }

  async function handleDelete(gc: GapConfiguration) {
    if (!confirm(`Supprimer "${gc.name}" ?`)) return;
    await deleteMut.mutateAsync(gc.id);
  }

  const presets = configs.filter(c => c.isPreset);
  const custom  = configs.filter(c => !c.isPreset);

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-sm font-semibold">Configurations GAP</CardTitle>
            <CardDescription className="text-xs mt-0.5">
              Combinaisons nommées type · dimension · segmentation · devise.
              Tous les rapports ALM référencent une configuration GAP.
            </CardDescription>
          </div>
          {isAdmin && !showForm && (
            <Button size="sm" variant="outline" onClick={openCreate} className="h-7 text-xs gap-1">
              <Plus className="h-3 w-3" /> Nouveau
            </Button>
          )}
        </div>
      </CardHeader>

      <CardContent className="space-y-3">

        {/* ── Form ── */}
        {showForm && (
          <div className="rounded border border-dashed border-cx-navy/30 bg-slate-50 p-3 space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-xs font-semibold text-cx-navy">
                {editing ? 'Modifier la configuration' : 'Nouvelle configuration GAP'}
              </p>
              <button onClick={closeForm} className="text-slate-400 hover:text-slate-600">
                <X className="h-3.5 w-3.5" />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div className="col-span-2">
                <Label className="text-xs">Nom *</Label>
                <Input className="h-7 text-xs mt-0.5" value={form.name}
                  onChange={e => f('name', e.target.value)} placeholder="Ex: Liquidité standard MAD" />
              </div>

              <div className="col-span-2">
                <Label className="text-xs">Description</Label>
                <Input className="h-7 text-xs mt-0.5" value={form.description ?? ''}
                  onChange={e => f('description', e.target.value)} />
              </div>

              <div>
                <Label className="text-xs">Type de GAP *</Label>
                <select className="mt-0.5 w-full rounded border border-input bg-background px-2 py-1 text-xs"
                  value={form.gapType}
                  onChange={e => f('gapType', e.target.value as GapType)}>
                  {(Object.keys(GAP_TYPE_LABELS) as GapType[]).map(t => (
                    <option key={t} value={t}>{GAP_TYPE_LABELS[t]}</option>
                  ))}
                </select>
              </div>

              <div>
                <Label className="text-xs">Mode par défaut</Label>
                <select className="mt-0.5 w-full rounded border border-input bg-background px-2 py-1 text-xs"
                  value={form.presentationMode}
                  onChange={e => f('presentationMode', e.target.value as PresentationMode)}>
                  {(Object.keys(PRESENTATION_MODE_LABELS) as PresentationMode[]).map(m => (
                    <option key={m} value={m}>{PRESENTATION_MODE_LABELS[m]}</option>
                  ))}
                </select>
              </div>

              <div>
                <Label className="text-xs">Dimension pivot</Label>
                <select className="mt-0.5 w-full rounded border border-input bg-background px-2 py-1 text-xs"
                  value={form.rowDimension}
                  onChange={e => f('rowDimension', e.target.value as RowDimension)}>
                  {(Object.keys(ROW_DIMENSION_LABELS) as RowDimension[]).map(d => (
                    <option key={d} value={d}>{ROW_DIMENSION_LABELS[d]}</option>
                  ))}
                </select>
              </div>

              <div>
                <Label className="text-xs">Devise (vide = toutes)</Label>
                <Input className="h-7 text-xs mt-0.5"
                  value={form.currency ?? ''}
                  onChange={e => f('currency', e.target.value || null)}
                  placeholder="MAD" />
              </div>
            </div>

            {formErr && <p className="text-xs text-red-500">{formErr}</p>}
            <ApiErr err={(createMut.error || updateMut.error) as CubixApiError | null} />

            <div className="flex gap-2 justify-end">
              <Button size="sm" variant="ghost" onClick={closeForm} className="h-7 text-xs">
                Annuler
              </Button>
              <Button size="sm" onClick={handleSubmit}
                disabled={createMut.isPending || updateMut.isPending}
                className="h-7 text-xs">
                {editing ? 'Enregistrer' : 'Créer'}
              </Button>
            </div>
          </div>
        )}

        {/* ── Loading ── */}
        {isLoading && (
          <p className="text-xs text-slate-400 text-center py-4">Chargement…</p>
        )}

        {/* ── Presets ── */}
        {presets.length > 0 && (
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1.5">
              Presets système
            </p>
            <div className="space-y-1">
              {presets.map(gc => (
                <GapConfigRow key={gc.id} gc={gc}
                  usage={usageMap[gc.id] ?? 0}
                  isAdmin={false}
                  onEdit={() => {}} onDelete={() => {}} />
              ))}
            </div>
          </div>
        )}

        {/* ── Custom ── */}
        {custom.length > 0 && (
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1.5">
              Configurations personnalisées
            </p>
            <div className="space-y-1">
              {custom.map(gc => (
                <GapConfigRow key={gc.id} gc={gc}
                  usage={usageMap[gc.id] ?? 0}
                  isAdmin={isAdmin}
                  onEdit={() => openEdit(gc)}
                  onDelete={() => handleDelete(gc)} />
              ))}
            </div>
          </div>
        )}

        {!isLoading && configs.length === 0 && (
          <p className="text-xs text-slate-400 text-center py-4">
            Aucune configuration GAP. Cliquez sur "Nouveau" pour en créer une.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

// ── Row sub-component ─────────────────────────────────────────────────────────

function GapConfigRow({
  gc, usage, isAdmin, onEdit, onDelete,
}: {
  gc: GapConfiguration;
  usage: number;
  isAdmin: boolean;
  onEdit: () => void;
  onDelete: () => void;
}) {
  return (
    <div className="flex items-center gap-2 rounded bg-white border border-slate-100 px-3 py-2 text-xs">
      {gc.isPreset && (
        <span title="Preset système"><Shield className="h-3 w-3 shrink-0 text-cx-navy/40" /></span>
      )}

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium truncate">{gc.name}</span>
          <TypeBadge type={gc.gapType} />
          <ModeBadge mode={gc.presentationMode} />
        </div>
        <div className="text-slate-400 text-[10px] mt-0.5 flex gap-2 flex-wrap">
          <span>{ROW_DIMENSION_LABELS[gc.rowDimension]}</span>
          {gc.currency && <span>· {gc.currency}</span>}
          {usage > 0 && <span className="text-slate-500">· {usage} rapport{usage > 1 ? 's' : ''}</span>}
        </div>
      </div>

      {isAdmin && !gc.isPreset && (
        <div className="flex gap-1 shrink-0">
          <button onClick={onEdit}
            className="p-1 rounded hover:bg-slate-100 text-slate-400 hover:text-cx-navy transition-colors">
            <Edit2 className="h-3 w-3" />
          </button>
          <button onClick={onDelete}
            className="p-1 rounded hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors">
            <Trash2 className="h-3 w-3" />
          </button>
        </div>
      )}
    </div>
  );
}

function TypeBadge({ type }: { type: GapType }) {
  const colors = {
    LIQUIDITY:     'bg-blue-50 text-blue-700',
    INTEREST_RATE: 'bg-amber-50 text-amber-700',
  };
  return (
    <span className={`rounded px-1.5 py-0.5 text-[9px] font-semibold ${colors[type]}`}>
      {GAP_TYPE_LABELS[type]}
    </span>
  );
}

function ModeBadge({ mode }: { mode: PresentationMode }) {
  return (
    <span className="rounded bg-slate-100 text-slate-600 px-1.5 py-0.5 text-[9px]">
      {mode === 'FLOW' ? 'Tombée' : 'Encours'}
    </span>
  );
}
